import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "../../shared";
import { getMyCards } from "../../../services/cards";
import { CardVisual } from "../cards/CardVisual";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";

/**
 * Сколько карт показываем на главной. Всё, что сверху лимита, скрывается
 * под fade-градиентом с кнопкой «Показать ещё» — юзер уводится на /cards.
 * Порог намеренно небольшой (2), иначе главная разъезжается по вертикали
 * при десятках карт.
 */
const PREVIEW_LIMIT = 2;

interface WalletCardsSectionProps {
  isLoading?: boolean;
}

/**
 * Секция «Мои карты» на главной странице кошелька.
 *
 * • Если у юзера уже есть выпущенные карты — показываем превью каждой
 *   (визуально идентично `MyCardsSection` на /cards).
 * • Если карт нет — рисуем empty-state в нашем стиле: иконка + текст +
 *   кнопка «Открыть карту», уводящая на /cards.
 */
export function WalletCardsSection({ isLoading: parentLoading }: WalletCardsSectionProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("home");
  const { guardFeature, isFeatureEnabled } = useFeatureFlags();

  const { data, isLoading: queryLoading } = useQuery({
    queryFn: getMyCards,
    queryKey: ["my-cards"],
    staleTime: 30_000,
    // Не грузим карты, если раздел выключен админом.
    enabled: isFeatureEnabled("cards"),
  });

  const openCardsList = () => {
    if (!guardFeature("cards")) return;
    navigate("/cards");
  };
  const openCardIssue = () => {
    // «Открыть карту» — отдельный флаг: могут разрешить смотреть карты,
    // но временно закрыть выпуск новых.
    if (!guardFeature("card_issue")) return;
    navigate("/cards");
  };

  const cards = data ?? [];
  const isLoading = parentLoading || queryLoading;

  const previewCards = cards.slice(0, PREVIEW_LIMIT);
  const hiddenCount = Math.max(0, cards.length - PREVIEW_LIMIT);
  const peekCard = hiddenCount > 0 ? cards[PREVIEW_LIMIT] : null;

  return (
    <Box>
      {isLoading ? (
        <Skeleton
          variant="rounded"
          width={120}
          height={22}
          sx={{ borderRadius: "8px", marginBottom: "14px", marginLeft: "4px" }}
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "14px",
            paddingLeft: "4px",
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 600,
            }}
          >
            {t("sections.my_cards")}
          </Typography>

          <IconButton
            onClick={openCardIssue}
            aria-label={t("sections.issue_card_aria")}
            sx={{
              width: 36,
              height: 36,
              color: "text.primary",
              bgcolor: "transparent",
              "&:hover": { bgcolor: "transparent" },
            }}
          >
            <Icon icon="solar:add-square-outline" width={26} height={26} />
          </IconButton>
        </Box>
      )}

      <Box sx={{ display: "grid", gap: "12px" }}>
        {isLoading ? (
          <>
            <Skeleton
              variant="rounded"
              height={170}
              sx={{ borderRadius: "20px" }}
            />
            <Skeleton
              variant="rounded"
              height={170}
              sx={{ borderRadius: "20px" }}
            />
          </>
        ) : cards.length === 0 ? (
          <EmptyCardsState onOpen={openCardIssue} />
        ) : (
          previewCards.map((card) => (
            <ButtonBase
              key={card.id}
              onClick={() => {
                if (!guardFeature("cards")) return;
                navigate(`/cards/${card.id}`);
              }}
              sx={{
                display: "block",
                textAlign: "left",
                borderRadius: "20px",
                transition: "transform 120ms ease",
                "&:active": { transform: "scale(0.985)" },
              }}
            >
              <CardVisual card={card} />
            </ButtonBase>
          ))
        )}
      </Box>

      {!isLoading && peekCard && (
        <Box sx={{ marginTop: "12px" }}>
          <ButtonBase
            onClick={openCardsList}
            sx={{
              display: "block",
              width: "100%",
              textAlign: "left",
              borderRadius: "20px",
              "&:active .peek-inner": { transform: "scale(0.99)" },
            }}
          >
            <Box
              sx={{
                position: "relative",
                // Показываем верхнюю ~40% карты — этого хватает, чтобы намекнуть
                // «дальше есть ещё», но не занять всю главную.
                height: { xs: "110px", sm: "130px" },
                overflow: "hidden",
                borderRadius: "20px",
              }}
            >
              <Box
                className="peek-inner"
                sx={{ transition: "transform 120ms ease" }}
              >
                <CardVisual card={peekCard} />
              </Box>
              {/* Fade-оверлей: transparent → background.default. Так он бесшовно
                 сливается с фоном страницы в обеих темах — под dark #0F0F12
                 получается «чёрно-прозрачный», под light #F5F5F7 — «бело-
                 прозрачный», ровно как просил юзер. */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  background: (theme) => `linear-gradient(180deg,
                    ${alpha(theme.palette.background.default, 0)} 0%,
                    ${alpha(theme.palette.background.default, 0.55)} 45%,
                    ${alpha(theme.palette.background.default, 0.95)} 85%,
                    ${theme.palette.background.default} 100%)`,
                }}
              />
              {/* Пилюля-кнопка поверх fade-градиента. */}
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  bottom: "10px",
                  transform: "translateX(-50%)",
                  paddingX: "16px",
                  paddingY: "8px",
                  borderRadius: "999px",
                  bgcolor: "secondary.main",
                  color: "text.primary",
                  fontSize: "13px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                {t("sections.show_more_cards", { count: hiddenCount })}
              </Box>
            </Box>
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------
// Empty-state: у пользователя ещё нет карт.
// ---------------------------------------------------------------

interface EmptyCardsStateProps {
  onOpen: () => void;
}

function EmptyCardsState({ onOpen }: EmptyCardsStateProps) {
  const { t } = useTranslation("home");
  return (
    <Box
      sx={(theme) => ({
        borderRadius: "20px",
        padding: "22px 18px",
        bgcolor: "secondary.main",
        border:
          theme.palette.mode === "light"
            ? "1px solid rgba(0, 0, 0, 0.05)"
            : "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "12px",
        textAlign: "center",
      })}
    >
      <Box
        sx={(theme) => ({
          width: 56,
          height: 56,
          borderRadius: "18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.05)",
          color: "text.primary",
        })}
      >
        <Icon
          icon="solar:card-2-bold-duotone"
          width={32}
          height={32}
        />
      </Box>

      <Box sx={{ display: "grid", gap: "4px" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
          {t("sections.no_cards_title")}
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            color: "text.secondary",
            maxWidth: "280px",
            margin: "0 auto",
          }}
        >
          {t("sections.no_cards_subtitle")}
        </Typography>
      </Box>

      <ButtonBase
        onClick={onOpen}
        sx={(theme) => ({
          marginTop: "4px",
          padding: "10px 22px",
          borderRadius: "40px",
          fontSize: "14px",
          fontWeight: 500,
          fontFamily: "Inter",
          bgcolor: theme.palette.mode === "dark" ? "#26262A" : "#e0e0e0",
          color: theme.palette.mode === "dark" ? "#fff" : "#000",
          transition: "opacity 0.15s",
          "&:active": { opacity: 0.85 },
        })}
      >
        {t("sections.open_card")}
      </ButtonBase>
    </Box>
  );
}
